import { Deletable } from '../interface/deletable';
import { HasCountryCode } from '../interface/has-country-code';
import { Identifiable } from '../interface/identifiable';
import { I18nString } from '../class/i18nstring';
import { Factory } from '../interface/factory';
import { CountryCode } from '../enum/country-code';
import { Alias } from './user';
import { Email } from './email';
import { CronSettings, OrganizationPaymentMethod, PaymentExecutionConfiguration, BillingSchedule } from '..';
import { ProductSource } from '../enum/product-source';

export interface OrganizationAddress {
  address?: string;
  postcode?: string;
  city?: string;
}

export class Organization implements Deletable, Identifiable, HasCountryCode {
  id: string;
  capabilities?: Capabilities;

  deleted: boolean;

  countryCode: CountryCode;

  parentIds: string[]; // NOTE: Lower index is higher in the hierarchy.
  names: I18nString[];
  logo?: string;

  userAliasIdCounter?: number;
  productAliasIdCounter?: number;
  invoiceNumberCounter?: number;
  voucherNumberCounter?: number;

  invoiceFee?: number;

  timezone: string;

  address?: OrganizationAddress;

  /** Legal organization number (CVR, org.nr). */
  organizationNumber?: string;

  created: Date;
  modified: Date;

  constructor(json: any) {
    this.id = json.id;
    this.capabilities = json.capabilities ? new Capabilities(json.capabilities) : undefined;

    this.deleted = json.deleted ? Boolean(json.deleted) : false;

    this.countryCode = json.countryCode as CountryCode;

    this.parentIds = json.parentIds ? json.parentIds : [];

    this.names = json.names ? json.names.map((json: any) => new I18nString(json)) : [];
    this.logo = json.logo ? json.logo : undefined;

    this.userAliasIdCounter = json.userAliasIdCounter !== undefined ? Number(json.userAliasIdCounter) : undefined;
    this.productAliasIdCounter =
      json.productAliasIdCounter !== undefined ? Number(json.productAliasIdCounter) : undefined;
    this.invoiceNumberCounter = json.invoiceNumberCounter !== undefined ? Number(json.invoiceNumberCounter) : undefined;
    this.voucherNumberCounter = json.voucherNumberCounter !== undefined ? Number(json.voucherNumberCounter) : undefined;
    this.invoiceFee = json.invoiceFee !== undefined ? Number(json.invoiceFee) : undefined;

    this.timezone = json.timezone;

    this.address = json.address ? json.address : undefined;

    this.organizationNumber = json.organizationNumber ?? undefined;

    this.created = new Date(json.created);
    this.modified = new Date(json.modified);
  }

  static getFactory(): Factory<Organization> {
    return new (class implements Factory<Organization> {
      make(json: any): Organization {
        return new Organization(json);
      }
      getTableName(): string {
        return 'organizations';
      }
    })();
  }

  static getUrl(orgId?: string): string;
  static getUrl(orgId: string): string {
    return '/organizations' + (orgId ? '/' + orgId : '');
  }

  getName(): string {
    return this.names.length > 0 ? this.names[0].value : '';
  }

  getIconFileName(): string {
    return this.countryCode.toLocaleLowerCase() + '.svg';
  }
}

export class Capabilities {
  issuer?: OrganizationIssuerConfiguration;
  reseller?: OrganizationResellerConfiguration;
  customer?: OrganizationCustomerConfiguration;

  constructor(json: any) {
    this.issuer = json.issuer ? new OrganizationIssuerConfiguration(json.issuer) : undefined;
    this.reseller = json.reseller ? new OrganizationResellerConfiguration(json.reseller) : undefined;
    this.customer = json.customer ? new OrganizationCustomerConfiguration(json.customer) : undefined;
  }
}

export class OrganizationIssuerConfiguration {
  cron: CronSettings;
  paymentExecutionConfiguration?: PaymentExecutionConfiguration;

  constructor(json: any) {
    this.cron = new CronSettings(json.cron);
    this.paymentExecutionConfiguration = json.paymentExecutionConfiguration
      ? new PaymentExecutionConfiguration(json.paymentExecutionConfiguration)
      : undefined;
  }
}

/** How a reseller numbers its customers. The issuer alias is always allocated as well. */
export enum ResellerCustomerAliasMode {
  None = 'none',
  Internal = 'internal',
  External = 'external',
}

export class OrganizationResellerConfiguration {
  customerAliasMode: ResellerCustomerAliasMode;
  /** Reseller's customer number at its issuer (reference only). */
  issuerCustomerNumber?: string;

  constructor(json: Partial<OrganizationResellerConfiguration> & { customerAliasMode?: string }) {
    const mode = json.customerAliasMode ?? ResellerCustomerAliasMode.None;
    this.customerAliasMode =
      mode === ResellerCustomerAliasMode.Internal ||
      mode === ResellerCustomerAliasMode.External ||
      mode === ResellerCustomerAliasMode.None
        ? mode
        : ResellerCustomerAliasMode.None;
    this.issuerCustomerNumber = json.issuerCustomerNumber ?? undefined;
  }
}

/** Which purchase clients may buy for this business customer (backoffice is always allowed). */
export class CustomerPurchaseChannels {
  myPages: boolean;
  webshop: boolean;
  app: boolean;
  external: boolean;
  register: boolean;

  constructor(json?: Partial<CustomerPurchaseChannels>) {
    this.myPages = json?.myPages !== false;
    this.webshop = json?.webshop !== false;
    this.app = json?.app !== false;
    this.external = json?.external !== false;
    this.register = json?.register !== false;
  }
}

export class OrganizationCustomerConfiguration {
  static readonly maxDiscountCodes = 20;

  customerAliasIds: Alias[];
  paymentMethods: OrganizationPaymentMethod[];
  billingSchedule: BillingSchedule;
  companyEmail?: Email;
  invoiceEmail?: string;
  /** Legacy single code; folded into {@link discountCodes} on read. */
  discount?: string;
  discounts: string[];
  purchaseChannels: CustomerPurchaseChannels;
  /** Reseller this customer signed up through; `undefined` for direct issuer customers. */
  resellerOrganizationId?: string;

  constructor(json: any) {
    this.customerAliasIds = json.customerAliasIds ? json.customerAliasIds.map((a: any) => new Alias(a)) : [];
    this.paymentMethods = json.paymentMethods
      ? json.paymentMethods.map((o: any) => new OrganizationPaymentMethod(o))
      : [];
    this.billingSchedule = new BillingSchedule(json.billingSchedule);

    this.companyEmail = json.companyEmail ? Email.getFactory().make(json.companyEmail) : undefined;
    this.invoiceEmail = json.invoiceEmail ? json.invoiceEmail : undefined;
    this.discount = json.discount ? json.discount : undefined;
    this.discounts = Array.isArray(json.discounts) ? json.discounts.map(String) : [];
    this.purchaseChannels = new CustomerPurchaseChannels(json.purchaseChannels);
    this.resellerOrganizationId = json.resellerOrganizationId ?? undefined;
  }

  aliasIdForOrganization(organizationId: string): string | undefined {
    return this.customerAliasIds.find((a) => a.organizationId === organizationId)?.id;
  }

  /** Normalized codes from `discounts` plus legacy `discount` when missing from the list. */
  discountCodes(): string[] {
    return OrganizationCustomerConfiguration.normalizeCodes([
      ...this.discounts,
      ...(this.discount ? [this.discount] : []),
    ]);
  }

  static normalizeCodes(codes: string[]): string[] {
    const out: string[] = [];
    for (const code of codes) {
      const trimmed = code.trim();
      if (!trimmed) {
        continue;
      }
      if (out.some((existing) => existing.toLowerCase() === trimmed.toLowerCase())) {
        continue;
      }
      out.push(trimmed);
    }
    return out;
  }

  allowsPurchase(source: ProductSource): boolean {
    if (source === ProductSource.Backoffice) {
      return true;
    }
    switch (source) {
      case ProductSource.MyPages:
        return this.purchaseChannels.myPages;
      case ProductSource.Webshop:
        return this.purchaseChannels.webshop;
      case ProductSource.App:
        return this.purchaseChannels.app;
      case ProductSource.External:
        return this.purchaseChannels.external;
      case ProductSource.Register:
        return this.purchaseChannels.register;
      default:
        return true;
    }
  }
}
